import { ConfigurationContext } from 'components/provider/OrginazationContext';
import Modal from 'components/ui/Modal/modal';
import PrimaryButton from 'components/ui/PrimaryButton';
import SecondaryButton from 'components/ui/SecondaryButton';
import { useContext } from 'react';
import { useGetTraineeAttendanceByRoutineId } from 'service/admin.query';
import * as XLSX from 'xlsx';

interface DownloadTraineeAttendanceProps {
  routineId: number;
  open: boolean;
  toggle: () => void;
  routineDetail: any
}

// Format ISO date string to "YYYY-MM-DD"
const formatDate = (iso: string) => iso?.split('T')[0] ?? iso;

// Format "YYYY-MM-DD" as "YYYY/MM/DD" for display
const formatDateSlash = (dateKey: string) => dateKey.replace(/-/g, '/');

// Extract "HH:MM" from ISO datetime string
const formatTime = (iso: string | null): string => {
  if (!iso) return '-';
  const timePart = iso.split('T')[1];
  if (!timePart) return '-';
  return timePart.slice(0, 5); // "HH:MM"
};

// Add `days` to a "YYYY-MM-DD" key without local-timezone drift
const addDays = (dateKey: string, days: number): string => {
  const [y, m, d] = dateKey.split('-').map(Number);
  const date = new Date(Date.UTC(y, m - 1, d));
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().split('T')[0];
};

// Build every "YYYY-MM-DD" date between start and end (inclusive)
const getDateRange = (start: string, end: string): string[] => {
  const startKey = formatDate(start ?? '');
  const endKey = formatDate(end ?? '');
  if (!startKey || !endKey) return [];
  const dates: string[] = [];
  let current = startKey;
  while (current <= endKey) {
    dates.push(current);
    current = addDays(current, 1);
  }
  return dates;
};

// Date an attendance record belongs to, keyed as "YYYY-MM-DD"
const getAttendanceDateKey = (att: any): string =>
  formatDate(att.check_in ?? att.requested_date ?? '');

// Check-in/check-out display values for a single attendance record
const getAttendanceTimes = (att: any): { checkIn: string; checkOut: string } => {
  if (att.source === 'manual') {
    return { checkIn: 'Present', checkOut: '-' };
  }
  return { checkIn: formatTime(att.check_in), checkOut: formatTime(att.check_out) };
};

// Returns combined display value based on source type (used in the preview table)
const getAttendanceValue = (att: any): string => {
  const { checkIn, checkOut } = getAttendanceTimes(att);
  if (att.source === 'manual') return checkIn;
  return `${checkIn} - ${checkOut}`;
};

const DownloadTraineeAttendance = ({ routineId, open, toggle, routineDetail }: DownloadTraineeAttendanceProps) => {
  const { appConfig } = useContext(ConfigurationContext)

  const { data = [], isLoading } = useGetTraineeAttendanceByRoutineId(
    routineId,
    open && !!routineId
  );
  const routineStartDate = routineDetail?.start_date
  const routineEndDate = routineDetail?.end_date
  const routineName = routineDetail?.training_details?.training_name
  const hallName = routineDetail?.hall_details?.hall_name ?? ""
  const routinePeriod = `${routineStartDate ? formatDateSlash(formatDate(routineStartDate)) : '-'} to ${routineEndDate ? formatDateSlash(formatDate(routineEndDate)) : '-'}`

  const allAttendanceCols: string[] = getDateRange(routineStartDate, routineEndDate);

  const getCellValue = (trainee: any, dateKey: string): { value: string; source: string } => {
    const atts = (trainee.attendances ?? []).filter(
      (a: any) => getAttendanceDateKey(a) === dateKey
    );
    if (!atts.length) return { value: '-', source: '' };
    return {
      value: atts.map(getAttendanceValue).join('; '),
      source: atts[0].source ?? ''
    };
  };

  const buildRow = (trainee: any, index: number): Record<string, any> => {
    const row: Record<string, any> = {
      'S.N.': index + 1,
      'Full Name': trainee.full_name ?? '-',
      "TMS Code": trainee.attendance_pin ?? '-',
      // "Employee ID": trainee.user_id ?? '-',
      // Gender: trainee.gender ?? '-',
      Email: trainee.email ?? '-',

      'Mobile No.': trainee.mobile_number ?? '-',
      'Total Present': (trainee.attendances ?? []).filter((a: any) => a.status === true).length,
      'Total Absent': (trainee.attendances ?? []).filter((a: any) => a.status === false).length
    };
    allAttendanceCols.forEach((dateKey) => {
      row[formatDateSlash(dateKey)] = getCellValue(trainee, dateKey).value;
    });
    return row;
  };

  const staticHeaders = [
    'S.N.',
    'Full Name',
    "TMS Code",
    // "Employee ID",
    // 'Gender',
    'Email',
    'Mobile No.',
    'Total Present',
    'Total Absent'
  ];
  // One combined column per date (e.g. "09:29 - 14:17"); used by both the preview table and the Excel export
  const displayAttendanceCols = allAttendanceCols.map(formatDateSlash);
  const tableHeaders = [...staticHeaders, ...displayAttendanceCols];

  const handleDownload = () => {
    if (!(data as any[]).length) return;

    const exportData = (data as any[]).map((trainee: any, idx: number) => buildRow(trainee, idx));

    // Letterhead rows shown above the data table
    const headerInfoRows = [
      [appConfig?.title || '-'],
      [appConfig?.address || '-'],
      [`Routine Name: ${routineName || '-'}`],
      [`Hall: ${hallName || '-'}`],
      [`Duration: ${routinePeriod}`],
      []
    ];

    const worksheet = XLSX.utils.aoa_to_sheet(headerInfoRows);
    XLSX.utils.sheet_add_json(worksheet, exportData, { origin: -1, header: tableHeaders });

    // Merge each letterhead row across all data columns
    worksheet['!merges'] = headerInfoRows
      .map((_, i) => i)
      .filter((i) => headerInfoRows[i].length)
      .map((i) => ({ s: { r: i, c: 0 }, e: { r: i, c: tableHeaders.length - 1 } }));

    // Auto-width columns
    const colWidths = tableHeaders.map((h) => ({ wch: Math.max(h.length, 10) }));
    worksheet['!cols'] = colWidths;

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Attendance');
    XLSX.writeFile(workbook, `Trainee_Attendance_${routineId}.xlsx`);
  };

  const totalPresent = (data as any[]).reduce(
    (sum: number, t: any) =>
      sum + (t.attendances ?? []).filter((a: any) => a.status === true).length,
    0
  );
  const totalAbsent = (data as any[]).reduce(
    (sum: number, t: any) =>
      sum + (t.attendances ?? []).filter((a: any) => a.status === false).length,
    0
  );

  return (
    <Modal
      title="Download Trainee Attendance"
      isModalOpen={open}
      closeModal={toggle}
      modalSize="modal-xl"
      renderFooter={() => (
        <div className="hstack gap-2 justify-content-end">
          <SecondaryButton onClick={toggle} title="Close" />
          <PrimaryButton
            type="button"
            title="Download Excel"
            iconName="ri-file-excel-2-line me-1"
            onClick={handleDownload}
            isLoading={isLoading}
            disabled={(data as any[]).length === 0}
          />
        </div>
      )}>
      <div className="p-3">
        {/* Letterhead */}
        <div className="text-center mb-3">
          <div className="fw-bold fs-5">{appConfig?.title || '-'}</div>
          <div className="text-muted small">{appConfig?.address || '-'}</div>
          <div className="small mt-1">
            <span className="fw-semibold">Routine:</span> {routineName || '-'}
            &nbsp;|&nbsp;
            <span className="fw-semibold">Hall:</span> {hallName || '-'}
            &nbsp;|&nbsp;
            <span className="fw-semibold">Duration:</span> {routinePeriod}
          </div>
        </div>

        {/* Summary badges */}
        {!isLoading && (data as any[]).length > 0 && (
          <div className="d-flex gap-3 mb-3">
            <span className="badge bg-success-subtle text-success border border-success fs-6 px-3 py-2">
              Total Present: {totalPresent}
            </span>
            <span className="badge bg-danger-subtle text-danger border border-danger fs-6 px-3 py-2">
              Total Absent: {totalAbsent}
            </span>
            {allAttendanceCols.length > 0 && (
              <span className="badge bg-primary-subtle text-primary border border-primary fs-6 px-3 py-2">
                Days: {allAttendanceCols.length}
              </span>
            )}
          </div>
        )}

        {/* Preview Table */}
        <div className="mb-2 fw-bold">
          Data Preview
          {allAttendanceCols.length > 0 && (
            <span className="text-muted fw-normal ms-2 small">
              Manual: Present &nbsp;|&nbsp; Device: Check-in – Check-out
            </span>
          )}
        </div>
        <div className="table-responsive border rounded" style={{ maxHeight: '400px' }}>
          <table className="table table-sm table-hover mb-0 text-nowrap">
            <thead className="table-secondary sticky-top">
              <tr>
                {tableHeaders.map((header, i) => (
                  <th key={i} className="small">
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={tableHeaders.length} className="text-center p-3">
                    Loading data...
                  </td>
                </tr>
              ) : (data as any[]).length === 0 ? (
                <tr>
                  <td colSpan={tableHeaders.length} className="text-center p-3">
                    No trainees found for this routine.
                  </td>
                </tr>
              ) : (
                (data as any[]).map((trainee: any, idx: number) => {
                  const row = buildRow(trainee, idx);
                  return (
                    <tr key={idx}>
                      {tableHeaders.map((header, i) => {
                        const colIndex = displayAttendanceCols.indexOf(header);
                        const isAttendanceCol = colIndex !== -1;
                        if (isAttendanceCol) {
                          const rawDateKey = allAttendanceCols[colIndex];
                          const { value, source } = getCellValue(trainee, rawDateKey);
                          const isManual = source === 'manual';
                          let cellClass = 'small text-center';
                          if (isManual) cellClass += ' text-success fw-bold';
                          else if (value !== '-') cellClass += ' text-primary';
                          return (
                            <td key={i} className={cellClass}>
                              {value}
                            </td>
                          );
                        }
                        return (
                          <td key={i} className="small text-center">
                            {row[header] ?? ''}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        <div className="p-2 bg-light text-center small text-muted border border-top-0 rounded-bottom">
          {(data as any[]).length} total trainee(s) will be exported.
        </div>
      </div>
    </Modal>
  );
};

export default DownloadTraineeAttendance;
